/**
 * SalesPOSPage - Kassza (Point of Sale) Főoldal
 *
 * Teljes kassza workflow:
 * 1. Session ellenőrzés/nyitás
 * 2. Termékkeresés (kézi + vonalkód)
 * 3. Kosárkezelés
 * 4. Fizetés feldolgozás
 * 5. Session zárás (Z-report)
 */

import { PartnerSearchSelect } from '@/components/partners';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import {
  useAddTransactionItem,
  useBarcodeScanner,
  useCompleteTransaction,
  useCreateTransaction,
  useCurrentSession,
  useFinalizeTransaction,
  useFindProductByBarcode,
  usePosLoyaltyBalance,
  useProcessCardPayment,
  useProcessCashPayment,
  useProductSearch,
  useRedeemLoyalty,
  useVoidTransaction,
} from '@/hooks/pos';
import { useAudioFeedback } from '@/hooks/use-audio-feedback';
import { useDefaultWarehouse } from '@/hooks/use-default-warehouse';
import { usePackagesByType } from '@/hooks/use-packages';
import { useCreateSalesProcurementRequest } from '@/hooks/use-procurement-requests';
import { useSale } from '@/hooks/use-sales';
import { getNavStatusBadge, isTerminalNavStatus } from '@/lib/nav-status';
import type { CashRegisterSession, SaleTransaction, ZReport } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SessionCloseModal,
  SessionOpenModal,
  SessionStatusBadge,
  SessionSuspendModal,
  ZReportView,
} from './components';
import { posPackageErrorToastKey } from './pos-package-error';
import { changeDue, isCashSufficient, loyaltyErrorToastKey } from './pos-payment';
import { useSalesStore } from './sales-store';
import type { Customer, Product } from './types';
import { PaymentMethod } from './types';
import { useAddPosPackageToCart } from './use-pos-package-cart';

// Loading spinner component
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-8 w-8' : 'h-6 w-6';
  return (
    <div
      className={`${sizeClass} animate-spin rounded-full border-2 border-gray-300 dark:border-[#2d7d8a] border-t-primary`}
    />
  );
}

// Error alert component
function ErrorAlert({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
      <div className="flex items-center gap-2">
        <span className="text-red-500">⚠️</span>
        <p className="text-sm text-red-700">{message}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="ml-auto">
            {retryLabel ?? t('common.retry')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function SalesPOSPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showSessionOpen, setShowSessionOpen] = useState(false);
  const [showSessionClose, setShowSessionClose] = useState(false);
  const [showSessionSuspend, setShowSessionSuspend] = useState(false);
  const [showZReport, setShowZReport] = useState(false);
  const [lastZReport, setLastZReport] = useState<ZReport | null>(null);
  const [currentTransaction, setCurrentTransaction] = useState<SaleTransaction | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  // R-4 NAV-chip: a sikeres checkout után megtartjuk az utolsó sale-tranzakció id-ját
  // + a számokat (összeg/visszajáró) — a cart-reset előtt rögzítve, hogy a success-panel
  // ne a már kiürített kosár 0-it mutassa. A NAV-státusz async derül ki (BE-outbox relay).
  const [lastSale, setLastSale] = useState<{ id: string; total: number; change: number } | null>(
    null
  );
  const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState(0);
  // S-126-05 slice 2: out-of-stock product captured from barcode scan, used by
  // the "Beszerzési kosárba" SALES procurement-request shortcut button.
  const [outOfStockProduct, setOutOfStockProduct] = useState<Product | null>(null);
  const [procurementAdded, setProcurementAdded] = useState(false);
  const createSalesProcurementRequest = useCreateSalesProcurementRequest();

  // Q-T043: SALES-csomagok a POS-katalógusban — kiválasztáskor a hook lekéri a BE
  // pos-expansion-t és 1 kosársorként (csomag-ár) teszi be (a készlet-dekrement BE-atomi).
  const { packages: salesPackages } = usePackagesByType('SALES');
  const { addPackage, isLoading: isAddingPackage } = useAddPosPackageToCart();

  const PRODUCT_CATEGORIES = useMemo(
    () => [
      { key: 'powerTool', label: t('sales.pos.productCategories.powerTool') },
      { key: 'handTool', label: t('sales.pos.productCategories.handTool') },
      { key: 'accessory', label: t('sales.pos.productCategories.accessory') },
      { key: 'workwear', label: t('sales.pos.productCategories.workwear') },
    ],
    [t]
  );

  // POS-session warehouse — a CashRegisterSession.locationId FK valójában Warehouse-ra
  // mutat (séma-csapda: a mezőnév „location"). A tenant alapértelmezett raktárát a JWT
  // tenantId-jából oldjuk fel; a User.locationId NEM jó ide (másik tábla + nincs a JWT-ben).
  const {
    warehouseId,
    isLoading: warehouseLoading,
    isEmpty: noDefaultWarehouse,
    isError: warehouseError,
    refetch: refetchWarehouse,
  } = useDefaultWarehouse();
  const locationId = warehouseId ?? '';

  // Audio feedback
  const { playScan, playSuccess, playError, playPayment } = useAudioFeedback();

  // API Hooks
  const {
    data: session,
    isLoading: sessionLoading,
    refetch: refetchSession,
  } = useCurrentSession(locationId);

  const createTransactionMutation = useCreateTransaction();
  const addItemMutation = useAddTransactionItem();
  const voidTransactionMutation = useVoidTransaction();
  const cashPaymentMutation = useProcessCashPayment();
  const cardPaymentMutation = useProcessCardPayment();
  const completeTransactionMutation = useCompleteTransaction();
  const finalizeTransactionMutation = useFinalizeTransaction();

  const redeemLoyaltyMutation = useRedeemLoyalty();

  const finalizeSale = async (transactionId: string) => {
    await finalizeTransactionMutation.mutateAsync(transactionId);
  };

  // R-4 NAV-chip: az utolsó sale NAV-státuszát a sale-response-ból olvassuk
  // (useSale → transaction.navStatus, nulla BE-change). A relay async oldja fel,
  // ezért a checkout után rövid ideig pending/NULL lehet → poll alább.
  const { transaction: lastSaleTransaction, refetch: refetchLastSale } = useSale(lastSale?.id);
  const lastSaleNavStatus = lastSaleTransaction?.navStatus;

  // Poll: amíg a NAV-státusz nem terminál (ACCEPTED/REJECTED/ERROR) ÉS a próbaszám
  // a cap alatt van, ~2.5 mp-enként refetch. A számláló ref-ben (re-render-immunis),
  // a cleanup törli az intervalt. Leáll: terminál-státusz VAGY MAX_NAV_POLLS elérve.
  const navPollAttemptsRef = useRef(0);
  useEffect(() => {
    if (!lastSale?.id) return;
    if (isTerminalNavStatus(lastSaleNavStatus)) return;

    const MAX_NAV_POLLS = 8;
    const POLL_INTERVAL_MS = 2500;
    if (navPollAttemptsRef.current >= MAX_NAV_POLLS) return;

    const timer = setInterval(() => {
      navPollAttemptsRef.current += 1;
      void refetchLastSale();
      if (navPollAttemptsRef.current >= MAX_NAV_POLLS) {
        clearInterval(timer);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [lastSale?.id, lastSaleNavStatus, refetchLastSale]);

  // Product search from API
  const { data: searchResults, isLoading: searchLoading } = useProductSearch(
    { search: searchTerm },
    { enabled: searchTerm.length >= 2 }
  );

  // Barcode product lookup
  const findByBarcode = useFindProductByBarcode();

  // Zustand store for local cart state
  const {
    cart,
    customer,
    paymentMethod,
    cashReceived,
    globalDiscount,
    getSubtotal,
    getTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    setCustomer,
    setPaymentMethod,
    setCashReceived,
    setGlobalDiscount,
    clearCart,
    reset,
  } = useSalesStore();

  // Loyalty balance for selected customer (SALES-FG-4-12)
  const { data: loyaltyData } = usePosLoyaltyBalance(customer?.id);

  // Handle barcode scan
  const handleBarcodeScan = useCallback(
    async (barcode: string) => {
      try {
        playScan();

        // Try to find product by barcode via API
        const result = await findByBarcode.mutateAsync(barcode);

        if (result) {
          // Convert POSProduct to local Product type and add to cart
          const product: Product = {
            id: result.id,
            sku: result.sku,
            name: result.name,
            category: result.category ?? t('common.other'),
            price: result.price,
            vatRate: result.vatRate,
            stock: result.stock,
            unit: result.unit,
            barcode: result.barcode ?? undefined,
          };

          if (product.stock > 0) {
            addToCart(product);
            playSuccess();
          } else {
            playError();
            setApiError(t('sales.pos.outOfStock'));
            // S-126-05 slice 2: surface the "Beszerzési kosárba" shortcut for
            // out-of-stock items. Cleared on add / banner-dismiss / 30s timeout.
            setOutOfStockProduct(product);
            setProcurementAdded(false);
          }
        } else {
          playError();
          setApiError(t('sales.pos.unknownBarcode', { barcode }));
        }
      } catch (error) {
        playError();
        setApiError(t('sales.pos.barcodeError'));
        console.error('Barcode scan error:', error);
      }
    },
    [findByBarcode, addToCart, playScan, playSuccess, playError, t]
  );

  // Setup barcode scanner - only when session is open
  useBarcodeScanner({
    onScan: handleBarcodeScan,
    onError: error => {
      setApiError(error);
      playError();
    },
    enabled: session?.status === 'OPEN',
    minLength: 5,
    maxGap: 50,
  });

  // Check if session needs to be opened on mount — csak ha van feloldott raktár,
  // különben a session-open üres locationId-vel hibára futna (empty-state-et mutatunk).
  useEffect(() => {
    if (warehouseId && !sessionLoading && !session) {
      setShowSessionOpen(true);
    }
  }, [warehouseId, sessionLoading, session]);

  // Clear API error after 5 seconds
  useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => setApiError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError]);

  // Filter products from API results
  const products = searchResults ?? [];
  const filteredProducts = products.filter(p => {
    const matchesSearch =
      searchTerm === '' ||
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.barcode && p.barcode.includes(searchTerm));
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }) as Product[];

  const handleProductClick = (product: Product) => {
    if (product.stock > 0) {
      addToCart(product);
      playSuccess();
    }
  };

  const handleCompleteSale = async () => {
    if (!session) {
      setApiError(t('sales.pos.noActiveSession'));
      return;
    }

    try {
      // Create transaction if not exists
      let transaction = currentTransaction;
      if (!transaction) {
        transaction = await createTransactionMutation.mutateAsync({
          sessionId: session.id,
          customerId: customer?.id,
        });
        setCurrentTransaction(transaction);

        // Add all cart items
        for (const item of cart) {
          await addItemMutation.mutateAsync({
            transactionId: transaction.id,
            dto: {
              productId: item.product.id,
              productCode: item.product.sku,
              productName: item.product.name,
              quantity: item.quantity,
              unitPrice: item.product.price,
              taxRate: item.product.vatRate,
              discountPercent: item.discount,
            },
          });
        }
      }

      // Mark transaction as ready for payment
      await completeTransactionMutation.mutateAsync(transaction.id);

      // SALES-FG-4-12: Redeem loyalty points first (partial VOUCHER payment)
      if (loyaltyPointsToRedeem > 0 && customer?.id) {
        try {
          await redeemLoyaltyMutation.mutateAsync({
            transactionId: transaction.id,
            partnerId: customer.id,
            pointsToRedeem: loyaltyPointsToRedeem,
          });
        } catch (redeemError) {
          // A loyalty-beváltás money-kritikus: a BE atomi deduct-ja elutasíthat
          // (elégtelen egyenleg / Serializable-ütközés — #603) → specifikus toast a
          // BE error.code-jára, és a fizetés NE induljon el (a pont nem vonódott le).
          playError();
          toast.error(t(loyaltyErrorToastKey(redeemError)));
          console.error('Loyalty redeem error:', redeemError);
          return;
        }
      }

      // Process remaining payment
      const total = getTotal();
      const remainingAfterLoyalty = total - loyaltyPointsToRedeem;
      if (paymentMethod === PaymentMethod.CASH) {
        await cashPaymentMutation.mutateAsync({
          transactionId: transaction.id,
          dto: { receivedAmount: remainingAfterLoyalty > 0 ? cashReceived : 0 },
        });
      } else if (paymentMethod === PaymentMethod.CARD) {
        if (remainingAfterLoyalty > 0) {
          await cardPaymentMutation.mutateAsync({
            transactionId: transaction.id,
            dto: { amount: remainingAfterLoyalty },
          });
        }
      }

      // Finalize transaction (deduct inventory)
      await finalizeSale(transaction.id);

      // Success! A számokat a cart-reset ELŐTT rögzítjük a success-panelhez.
      const finalChange = changeDue(cashReceived, total, loyaltyPointsToRedeem);
      playPayment();
      reset();
      setCurrentTransaction(null);
      setShowPayment(false);
      setLoyaltyPointsToRedeem(0);

      // R-4 NAV-chip: inline success-panel (alert helyett) — tranzakció-id + NAV-chip.
      // A poll-számláló resetelése az új sale-hez, hogy a frissen-kiállított számla
      // NAV-státuszát a teljes próba-ablakon át pollozhassuk.
      navPollAttemptsRef.current = 0;
      setLastSale({ id: transaction.id, total, change: finalChange });
    } catch (error) {
      playError();
      setApiError(t('sales.pos.paymentError'));
      console.error('Payment error:', error);
    }
  };

  const handleVoidTransaction = async () => {
    if (currentTransaction) {
      try {
        await voidTransactionMutation.mutateAsync({
          transactionId: currentTransaction.id,
          dto: { reason: t('sales.pos.voidReason') },
        });
        setCurrentTransaction(null);
        reset();
      } catch (error) {
        setApiError(t('sales.pos.voidError'));
        console.error('Void error:', error);
      }
    }
  };

  const handleSelectCustomer = (c: Customer | null) => {
    setCustomer(c);
    setShowCustomerSelect(false);
  };

  const handleSessionOpened = (_newSession: CashRegisterSession) => {
    setShowSessionOpen(false);
    refetchSession();
  };

  const handleSessionClosed = (zReport: ZReport) => {
    setShowSessionClose(false);
    setLastZReport(zReport);
    setShowZReport(true);
    refetchSession();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(LOCALE_MAP[locale] ?? 'hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const subtotal = getSubtotal();
  const total = getTotal();
  // bug-screen #3: a loyalty-beváltás csökkenti a kasszába fizetendőt — a gate +
  // visszajáró a loyalty UTÁNI összeghez igazodik (mint a handleComplete submit).
  const change = changeDue(cashReceived, total, loyaltyPointsToRedeem);
  const canCompleteSale =
    cart.length > 0 &&
    (paymentMethod !== PaymentMethod.CASH ||
      isCashSufficient(cashReceived, total, loyaltyPointsToRedeem));
  const isSessionOpen = session?.status === 'OPEN';
  const isLoading =
    createTransactionMutation.isPending ||
    cashPaymentMutation.isPending ||
    cardPaymentMutation.isPending ||
    completeTransactionMutation.isPending ||
    finalizeTransactionMutation.isPending;

  // Show session open modal if no active session
  if (warehouseLoading || sessionLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 dark:bg-[#1f5f6b]">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-[#ccfbf1]">{t('sales.pos.loadingSession')}</p>
        </div>
      </div>
    );
  }

  // A default-warehouse fetch elszállt (tranziens hálózati hiba) → blokkoló error-state
  // retry-vel, hogy a page ne essen át némán undefined raktárral (≠ az üres-állapot).
  if (warehouseError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 dark:bg-[#1f5f6b]">
        <div className="max-w-md px-6 text-center">
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {t('sales.pos.warehouseLoadError')}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button onClick={() => refetchWarehouse()}>{t('sales.pos.retry')}</Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // A tenantnak nincs alapértelmezett raktára → a kassza nem nyitható (a session-FK
  // Warehouse-t vár). Blokkoló empty-state a néma „üres success" helyett.
  if (noDefaultWarehouse) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 dark:bg-[#1f5f6b]">
        <div className="max-w-md px-6 text-center">
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {t('sales.pos.noDefaultWarehouse')}
          </p>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="mt-4">
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen flex-col bg-gray-100 dark:bg-[#1f5f6b]"
      data-testid="pos-interface"
    >
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <h1 className="text-xl font-bold">{t('sales.pos.title')}</h1>
            {session && <SessionStatusBadge session={session} />}
          </div>
          <div className="flex items-center gap-2">
            {/* Session controls */}
            {!session && (
              <Button onClick={() => setShowSessionOpen(true)} className="bg-green-600">
                {t('sales.pos.openRegister')}
              </Button>
            )}
            {session?.status === 'OPEN' && (
              <>
                <Button variant="outline" onClick={() => setShowSessionSuspend(true)}>
                  {t('sales.pos.suspend')}
                </Button>
                <Button variant="outline" onClick={() => setShowSessionClose(true)}>
                  {t('sales.pos.closeRegister')}
                </Button>
              </>
            )}
            {session?.status === 'SUSPENDED' && (
              <Button onClick={() => setShowSessionSuspend(true)} className="bg-blue-600">
                {t('sales.pos.resume')}
              </Button>
            )}

            {/* Customer badge */}
            {customer ? (
              <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-1">
                <span className="text-sm font-medium text-blue-700">{customer.name}</span>
                <button
                  onClick={() => setCustomer(null)}
                  className="text-blue-500 hover:text-blue-700"
                >
                  ✕
                </button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setShowCustomerSelect(true)}>
                {t('sales.pos.addCustomer')}
              </Button>
            )}
          </div>
        </div>

        {/* API Error display */}
        {apiError && (
          <div className="mt-2">
            <ErrorAlert
              message={apiError}
              onRetry={() => setApiError(null)}
              retryLabel={t('sales.pos.retry')}
            />
          </div>
        )}

        {/* R-4: checkout-success panel + NAV-státusz-chip (alert helyett, inline) */}
        {lastSale && (
          <div
            className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200"
            role="status"
            data-testid="pos-sale-success"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="font-semibold">{t('sales.pos.saleSuccessTitle')}</span>
              <span>
                {t('sales.pos.saleSuccessTotal')}: {formatPrice(lastSale.total)}
              </span>
              <span>
                {t('sales.pos.saleSuccessChange')}: {formatPrice(lastSale.change)}
              </span>
              <span className="font-mono text-xs text-green-700 dark:text-green-300">
                {t('sales.pos.saleTransactionLabel')}: {lastSale.id}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-700 dark:text-green-300">
                {t('navStatus.label')}:
              </span>
              {lastSaleNavStatus ? (
                getNavStatusBadge(lastSaleNavStatus, t)
              ) : (
                <span
                  className="flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                  data-testid="pos-nav-processing"
                >
                  <LoadingSpinner size="sm" />
                  {t('sales.pos.navProcessing')}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLastSale(null)}
                data-testid="pos-sale-success-dismiss"
              >
                {t('sales.pos.newSale')}
              </Button>
            </div>
          </div>
        )}

        {/* S-126-05 slice 2: out-of-stock → procurement cart shortcut */}
        {outOfStockProduct && !procurementAdded && (
          <div
            className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200"
            data-testid="pos-procurement-banner"
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium">
                {t('sales.pos.procurement.bannerTitle', { name: outOfStockProduct.name })}
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                {t('sales.pos.procurement.bannerHint')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={createSalesProcurementRequest.isPending}
                data-testid="pos-procurement-add-button"
                onClick={() => {
                  if (!outOfStockProduct) return;
                  void createSalesProcurementRequest
                    .mutateAsync({
                      productId: outOfStockProduct.id,
                      quantity: 1,
                      notes: `POS sale: ${outOfStockProduct.name}`,
                    })
                    .then(() => {
                      setProcurementAdded(true);
                      playSuccess();
                      // Auto-clear after a short success window.
                      setTimeout(() => {
                        setOutOfStockProduct(null);
                        setProcurementAdded(false);
                      }, 3000);
                    })
                    .catch((err: Error) => {
                      playError();
                      setApiError(err.message || t('sales.pos.procurement.addError'));
                    });
                }}
              >
                {createSalesProcurementRequest.isPending
                  ? t('common.saving')
                  : t('sales.pos.procurement.addButton')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOutOfStockProduct(null)}
                data-testid="pos-procurement-dismiss-button"
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
        {procurementAdded && (
          <div
            className="mt-2 rounded-lg bg-green-50 p-2 text-center text-sm text-green-700 dark:bg-green-900/30 dark:text-green-300"
            role="status"
            data-testid="pos-procurement-success"
          >
            {t('sales.pos.procurement.addSuccess')}
          </div>
        )}

        {/* Offline/Session warning */}
        {!isSessionOpen && session && (
          <div className="mt-2 rounded-lg bg-yellow-50 p-2 text-center text-sm text-yellow-700">
            {t('sales.pos.sessionWarning', {
              status:
                session.status === 'SUSPENDED'
                  ? t('sales.pos.sessionSuspended')
                  : t('sales.pos.sessionClosed'),
            })}
          </div>
        )}
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Products */}
        <div className="flex w-2/3 flex-col border-r dark:border-[#2d7d8a] bg-white dark:bg-[#1a4f5c]">
          {/* Search & Categories */}
          <div className="border-b p-4">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder={t('sales.pos.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pr-10"
                  disabled={!isSessionOpen}
                />
                {searchLoading && (
                  <div className="absolute right-3 top-2">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
              </div>
              {/* Barcode manual input field */}
              <div className="relative w-48">
                <Input
                  type="text"
                  placeholder={t('sales.pos.barcodePlaceholder')}
                  value={barcodeInput}
                  onChange={e => setBarcodeInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && barcodeInput.trim().length >= 3) {
                      void handleBarcodeScan(barcodeInput.trim());
                      setBarcodeInput('');
                    }
                  }}
                  className="pl-8"
                  disabled={!isSessionOpen}
                />
                <span className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                    />
                  </svg>
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                disabled={!isSessionOpen}
                className={`rounded-full px-3 py-1 text-sm ${
                  !selectedCategory
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-[#1f5f6b] text-gray-700 dark:text-[#ccfbf1] hover:bg-gray-200 dark:hover:bg-[#0f3d47]'
                } disabled:opacity-50`}
              >
                {t('sales.pos.allCategories')}
              </button>
              {PRODUCT_CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.label)}
                  disabled={!isSessionOpen}
                  className={`rounded-full px-3 py-1 text-sm ${
                    selectedCategory === cat.label
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-[#1f5f6b] text-gray-700 dark:text-[#ccfbf1] hover:bg-gray-200 dark:hover:bg-[#0f3d47]'
                  } disabled:opacity-50`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Product grid */}
          <div className="flex-1 overflow-auto p-4">
            {!isSessionOpen ? (
              <div className="flex h-full items-center justify-center text-gray-400 dark:text-[#99f6e4]">
                <div className="text-center">
                  <p className="text-lg">{t('sales.pos.openRegisterForProducts')}</p>
                  <Button onClick={() => setShowSessionOpen(true)} className="mt-4 bg-green-600">
                    {t('sales.pos.openRegister')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {salesPackages.length > 0 && (
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-[#ccfbf1]">
                      {t('sales.pos.packages.title')}
                    </h3>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
                      {salesPackages.map(pkg => (
                        <button
                          key={pkg.id}
                          onClick={() => {
                            void addPackage(pkg.id).catch((err: unknown) =>
                              toast.error(t(posPackageErrorToastKey(err)))
                            );
                          }}
                          disabled={isAddingPackage}
                          className="rounded-lg border border-primary/40 bg-primary/5 dark:bg-[#1a4f5c] p-3 text-left transition-all hover:border-primary hover:shadow-md disabled:opacity-50"
                        >
                          <p className="text-xs font-medium text-primary">
                            {t('sales.pos.packages.badge')}
                          </p>
                          <p className="mt-1 line-clamp-2 text-sm font-medium">{pkg.name}</p>
                          <div className="mt-2">
                            <span className="font-bold text-primary">
                              {pkg.fixedPrice != null ? formatPrice(pkg.fixedPrice) : '—'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div
                  className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4"
                  data-testid="product-search-results"
                >
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => handleProductClick(product)}
                      disabled={product.stock === 0}
                      className={`rounded-lg border p-3 text-left transition-all ${
                        product.stock === 0
                          ? 'cursor-not-allowed bg-gray-100 dark:bg-[#1f5f6b] opacity-60'
                          : 'bg-white dark:bg-[#1a4f5c] hover:border-primary hover:shadow-md'
                      }`}
                    >
                      <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">{product.sku}</p>
                      <p className="mt-1 line-clamp-2 text-sm font-medium">{product.name}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="font-bold text-primary">{formatPrice(product.price)}</span>
                        <span
                          className={`text-xs ${product.stock <= 3 ? 'text-red-500' : 'text-gray-500 dark:text-[#ccfbf1]'}`}
                        >
                          {product.stock} {product.unit}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Cart */}
        <div className="flex w-1/3 flex-col bg-gray-50 dark:bg-[#1f5f6b]">
          <div className="flex-1 overflow-auto p-4">
            <h2 className="mb-4 text-lg font-semibold">
              {t('sales.pos.cart', { count: cart.length })}
            </h2>

            {cart.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-gray-400 dark:text-[#99f6e4]">
                {t('sales.pos.cartEmpty')}
              </div>
            ) : (
              <div className="space-y-3" data-testid="cart-items">
                {cart.map(item => (
                  <div
                    key={item.product.id}
                    className="rounded-lg bg-white dark:bg-[#1a4f5c] p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.product.name}</p>
                        {item.product.category === 'CSOMAG' && (
                          <span className="mt-0.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            {t('sales.pos.packages.setBadge')}
                          </span>
                        )}
                        <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                          {formatPrice(item.product.price)} / {item.product.unit}
                        </p>
                        {item.packageOriginalSum != null &&
                          item.packageOriginalSum > item.product.price && (
                            <p className="text-xs font-medium text-green-600 dark:text-green-400">
                              {t('sales.pos.packages.savingsLabel')}: −
                              {formatPrice(item.packageOriginalSum - item.product.price)}
                            </p>
                          )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid="remove-cart-item"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 dark:bg-[#1f5f6b] hover:bg-gray-200 dark:hover:bg-[#0f3d47]"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium" data-testid="cart-item-qty">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          disabled={item.quantity >= item.product.stock}
                          className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 dark:bg-[#1f5f6b] hover:bg-gray-200 dark:hover:bg-[#0f3d47] disabled:opacity-50"
                        >
                          +
                        </button>
                      </div>
                      <span className="font-bold">{formatPrice(item.lineTotal)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart footer */}
          <div className="border-t dark:border-[#2d7d8a] bg-white dark:bg-[#1a4f5c] p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>{t('sales.pos.subtotal')}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {globalDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('sales.pos.discount')}</span>
                  <span>-{formatPrice(globalDiscount)}</span>
                </div>
              )}
              <div
                className="flex justify-between border-t pt-2 text-lg font-bold"
                data-testid="cart-total"
              >
                <span>{t('sales.pos.total')}</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  clearCart();
                  if (currentTransaction) {
                    handleVoidTransaction();
                  }
                }}
                disabled={cart.length === 0 || !isSessionOpen}
              >
                {t('sales.pos.clearCart')}
              </Button>
              <Button
                onClick={() => setShowPayment(true)}
                disabled={cart.length === 0 || !isSessionOpen}
                className="bg-green-600 hover:bg-green-700"
                data-testid="pos-pay-button"
              >
                {t('sales.pos.pay')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t('sales.pos.payment')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Payment method */}
                <div>
                  {/* Gomb-csoport: <label> helyett span+role=group — a label csak
                      form-vezerlohoz kotheto, gombokhoz aria-labelledby a helyes. */}
                  <span id="pos-payment-method-label" className="mb-2 block text-sm font-medium">
                    {t('payment.method')}
                  </span>
                  <div
                    role="group"
                    aria-labelledby="pos-payment-method-label"
                    className="grid grid-cols-3 gap-2 lg:grid-cols-5"
                  >
                    {[
                      { method: PaymentMethod.CASH, label: t('rental.cashPayment'), icon: '💵' },
                      { method: PaymentMethod.CARD, label: t('rental.cardPayment'), icon: '💳' },
                      {
                        method: PaymentMethod.TRANSFER,
                        label: t('rental.transferPayment'),
                        icon: '🏦',
                      },
                      {
                        method: PaymentMethod.VOUCHER,
                        label: t('sales.pos.voucherPayment'),
                        icon: '🎫',
                      },
                      {
                        method: PaymentMethod.CREDIT,
                        label: t('sales.pos.creditPayment'),
                        icon: '📋',
                      },
                    ].map(({ method, label, icon }) => (
                      <button
                        key={method}
                        onClick={() => setPaymentMethod(method)}
                        className={`rounded-lg border-2 p-3 text-center transition-all ${
                          paymentMethod === method
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-200 dark:border-[#2d7d8a] hover:border-gray-300 dark:hover:border-[#99f6e4]'
                        }`}
                      >
                        <span className="text-2xl">{icon}</span>
                        <p className="mt-1 text-sm font-medium">{label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Discount */}
                <div>
                  <label htmlFor="pos-global-discount" className="mb-2 block text-sm font-medium">
                    {t('sales.pos.discountFt')}
                  </label>
                  <Input
                    id="pos-global-discount"
                    type="number"
                    value={globalDiscount || ''}
                    onChange={e => setGlobalDiscount(Number(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>

                {/* Loyalty redemption (SALES-FG-4-12) */}
                {customer && loyaltyData?.data && loyaltyData.data.balance > 0 && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                        {t('sales.pos.loyalty.balance')}
                      </span>
                      <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                        {loyaltyData.data.balance} {t('sales.pos.loyalty.points')} (
                        {formatPrice(loyaltyData.data.balanceHuf)})
                      </span>
                    </div>
                    <div>
                      <label
                        htmlFor="pos-loyalty-redeem"
                        className="mb-1 block text-xs text-muted-foreground"
                      >
                        {t('sales.pos.loyalty.redeem')} (
                        {t('sales.pos.loyalty.maxRedemption', { percent: '50' })})
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="pos-loyalty-redeem"
                          type="number"
                          min={0}
                          max={Math.min(loyaltyData.data.balance, Math.floor(total * 0.5))}
                          value={loyaltyPointsToRedeem || ''}
                          onChange={e => {
                            const max = Math.min(loyaltyData.data.balance, Math.floor(total * 0.5));
                            const val = Math.min(Number(e.target.value) || 0, max);
                            setLoyaltyPointsToRedeem(val);
                          }}
                          placeholder="0"
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const max = Math.min(loyaltyData.data.balance, Math.floor(total * 0.5));
                            setLoyaltyPointsToRedeem(max);
                          }}
                        >
                          Max
                        </Button>
                      </div>
                      {loyaltyPointsToRedeem > 0 && (
                        <div className="mt-2 text-xs space-y-1">
                          <div className="flex justify-between text-amber-600 dark:text-amber-400">
                            <span>{t('sales.pos.loyalty.discount')}</span>
                            <span>-{formatPrice(loyaltyPointsToRedeem)}</span>
                          </div>
                          <div className="flex justify-between font-medium">
                            <span>{t('sales.pos.loyalty.remaining')}</span>
                            <span>{formatPrice(total - loyaltyPointsToRedeem)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cash received (only for cash payment) */}
                {paymentMethod === PaymentMethod.CASH && (
                  <div>
                    <label htmlFor="pos-cash-received" className="mb-2 block text-sm font-medium">
                      {t('sales.pos.receivedAmount')}
                    </label>
                    <Input
                      id="pos-cash-received"
                      type="number"
                      value={cashReceived || ''}
                      onChange={e => setCashReceived(Number(e.target.value) || 0)}
                      placeholder="0"
                      autoFocus
                    />
                    {/* Quick amounts */}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        total,
                        Math.ceil(total / 1000) * 1000,
                        Math.ceil(total / 5000) * 5000,
                        Math.ceil(total / 10000) * 10000,
                      ].map((amount, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCashReceived(amount)}
                          className="rounded bg-gray-100 dark:bg-[#1f5f6b] px-3 py-1 text-sm hover:bg-gray-200 dark:hover:bg-[#0f3d47]"
                        >
                          {formatPrice(amount)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t('sales.pos.toPay')}</span>
                      <span className="text-primary">{formatPrice(total)}</span>
                    </div>
                    {loyaltyPointsToRedeem > 0 && (
                      <div className="flex justify-between text-amber-600 dark:text-amber-400">
                        <span>{t('sales.pos.loyalty.discount')}</span>
                        <span>-{formatPrice(loyaltyPointsToRedeem)}</span>
                      </div>
                    )}
                    {loyaltyPointsToRedeem > 0 && (
                      <div className="flex justify-between font-medium">
                        <span>{t('sales.pos.loyalty.remaining')}</span>
                        <span>{formatPrice(total - loyaltyPointsToRedeem)}</span>
                      </div>
                    )}
                    {paymentMethod === PaymentMethod.CASH && cashReceived > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('sales.pos.change')}</span>
                        <span className="text-xl font-bold">{formatPrice(change)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowPayment(false)}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleCompleteSale}
                    disabled={!canCompleteSale || isLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        <span>{t('sales.detail.processing')}</span>
                      </div>
                    ) : (
                      t('sales.pos.completePayment')
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customer Select Modal */}
      {showCustomerSelect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{t('sales.pos.selectCustomer')}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* No customer option — keep this */}
              <button
                onClick={() => handleSelectCustomer(null)}
                className="mb-4 w-full rounded-lg border p-3 text-left hover:bg-gray-50 dark:hover:bg-[#0f3d47]"
              >
                <p className="font-medium text-muted-foreground">{t('sales.pos.noCustomer')}</p>
              </button>

              <PartnerSearchSelect
                variant="list"
                filter={{ status: 'ACTIVE' }}
                onSelect={partner => {
                  handleSelectCustomer({
                    id: partner.id,
                    name: partner.name,
                    type: partner.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
                    taxNumber: partner.taxNumber,
                    phone: partner.phone,
                    email: partner.email,
                  });
                }}
              />

              <Button
                variant="outline"
                onClick={() => setShowCustomerSelect(false)}
                className="mt-4 w-full"
              >
                {t('common.cancel')}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Session Modals */}
      <SessionOpenModal
        open={showSessionOpen}
        onOpenChange={setShowSessionOpen}
        onSessionOpened={handleSessionOpened}
        locationId={locationId}
      />

      <SessionCloseModal
        open={showSessionClose}
        onOpenChange={setShowSessionClose}
        session={session ?? undefined}
        onSessionClosed={handleSessionClosed}
      />

      <SessionSuspendModal
        open={showSessionSuspend}
        onOpenChange={setShowSessionSuspend}
        session={session ?? undefined}
        onSessionUpdated={() => refetchSession()}
      />

      {/* Z-Report View */}
      {showZReport && lastZReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[90vh] overflow-auto rounded-lg bg-white dark:bg-[#1a4f5c]">
            <ZReportView
              report={lastZReport}
              isOpen={showZReport}
              onClose={() => {
                setShowZReport(false);
                setLastZReport(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
